import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';

type BranchId = 'swiatniki' | 'mogilany';
type WeekdayNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7;

interface OpeningPeriod {
  days: WeekdayNumber[];
  open: string;
  close: string;
}

interface BranchContent {
  id: BranchId;
  kind: string;
  title: string;
  address: string;
  phoneLabel: string;
  phoneDisplay: string;
  phoneHref: string;
  extraPhone?: string;
  hours?: string[];
  openingPeriods: OpeningPeriod[];
  team: string[];
  teamNote: string;
  scope: string[];
  mapUrl: string;
  otherMapUrl: string;
}

interface BranchGalleryImage {
  src: string;
  srcSet: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, OnDestroy {
  selectedBranch: BranchId = 'swiatniki';
  currentTime = new Date();
  private readonly statusSoonThresholdMinutes = 60;

  private readonly branchGalleryImages: BranchGalleryImage[] = this.createBranchGalleryImages([
    'pexels-brit-212514-680994',
    'pexels-didsss-2332836',
    'pexels-ishtiyaq-pirzada-408241729-33239571',
    'pexels-ivan-petrov-2152765384-37809727',
    'pexels-jimmykphotos-28380812',
    'pexels-karl-none-2162052970-37950225',
    'pexels-lemuraw-13202206',
    'pexels-mclt-1036454',
    'pexels-muhammad-furqan-2150707883-32811873',
    'pexels-seurafrancis99-9826113',
    'pexels-sharon-snider-2339082-4520484',
    'pexels-stevedoessteve-31300120',
    'pexels-szafran-33127561',
    'pexels-szafran-35425205',
    'pexels-alessandro-antonio-serra-2148929360-33802186'
  ]);
  private branchGalleryIndex = 0;
  private branchGalleryTimerId?: number;
  private branchGalleryTransitionTimerId?: number;
  private statusTimerId?: number;
  private readonly loadedBranchGalleryImages = new Set<string>();
  private readonly failedBranchGalleryImages = new Set<string>();

  private readonly branchGalleryIntervalMs = 3500;
  private readonly branchGalleryFadeDurationMs = 850;

  currentBranchImage = this.branchGalleryImages[0];
  previousBranchImage = this.branchGalleryImages[0];
  isBranchImageTransitioning = false;

  readonly branchOrder: BranchId[] = ['swiatniki', 'mogilany'];

  readonly branches: Record<BranchId, BranchContent> = {
    swiatniki: {
      id: 'swiatniki',
      kind: 'Przychodnia',
      title: 'Świątniki Górne',
      address: 'ul. Bielowicza 57, 32-040 Świątniki Górne, woj. małopolskie',
      phoneLabel: 'Telefony',
      phoneDisplay: '12 346 17 07',
      phoneHref: '+48123461707',
      extraPhone: '+48 664 524 364',
      hours: [
        'Poniedziałek - Piątek: 8:00-20:00',
        'Sobota: 9:00-14:00',
        'Niedziela: 10:00-13:00'
      ],
      openingPeriods: [
        { days: [1, 2, 3, 4, 5], open: '8:00', close: '20:00' },
        { days: [6], open: '9:00', close: '14:00' },
        { days: [7], open: '10:00', close: '13:00' }
      ],
      team: [
        'lek. wet. Waldemar Grzechnik',
        'lek. wet. Wiktoria Jakubowska',
        'lek. wet. Mikołaj Włodek',
        'tech. wet. Paulina Słabczyk'
      ],
      teamNote: 'Nasz zespół pozostaje do Państwa dyspozycji i z pełnym zaangażowaniem dba o komfort oraz bezpieczeństwo każdego pacjenta.',
      scope: [
        'Pełny zakres opieki internistycznej i profilaktycznej.',
        'Diagnostyka laboratoryjna i obrazowa na miejscu.',
        'Opieka okołozabiegowa i wsparcie w trudnych decyzjach terapeutycznych.'
      ],
      mapUrl: 'https://www.google.com/maps/search/?api=1&query=ul.+Bielowicza+57,+Swiatniki+Gorne',
      otherMapUrl: 'https://www.google.com/maps/search/?api=1&query=Rynek+4,+Mogilany'
    },
    mogilany: {
      id: 'mogilany',
      kind: 'Gabinet',
      title: 'Mogilany',
      address: 'Rynek 4, 32-031 Mogilany, woj. małopolskie',
      phoneLabel: 'Rejestracja',
      phoneDisplay: '537 151 280',
      phoneHref: '+48537151280',
      hours: [
        'PN - PT: 16:00-20:00',
        'SOBOTA: 09:00-14:00',
        'Niedziela: Nieczynne'
      ],
      openingPeriods: [
        { days: [1, 2, 3, 4, 5], open: '16:00', close: '20:00' },
        { days: [6], open: '9:00', close: '14:00' }
      ],
      team: [
        'lek. wet. Waldemar Grzechnik',
        'lek. wet. Anna Góźdż',
        'lek. wet. Mikołaj Włodek',
        'lek. wet. Julia Fill'
      ],
      teamNote: 'Nasz zespół pozostaje do Państwa dyspozycji i służy profesjonalnym wsparciem na każdym etapie opieki nad zwierzęciem.',
      scope: [
        'Konsultacje i opieka profilaktyczna dla pacjentów lokalnych.',
        'Szczepienia, znakowanie, dobór profilaktyki i żywienia.',
        'Szybka ścieżka kontaktu i przekierowanie do szerszej diagnostyki w Świątnikach.'
      ],
      mapUrl: 'https://www.google.com/maps/search/?api=1&query=Rynek+4,+Mogilany',
      otherMapUrl: 'https://www.google.com/maps/search/?api=1&query=ul.+Bielowicza+57,+Swiatniki+Gorne'
    }
  };

  ngOnInit(): void {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes('mogilany')) {
      this.selectedBranch = 'mogilany';
    }

    this.loadedBranchGalleryImages.add(this.currentBranchImage.src);
    this.preloadBranchGalleryImages();
    this.statusTimerId = window.setInterval(() => {
      this.currentTime = new Date();
    }, 60_000);

    if (this.branchGalleryImages.length <= 1) {
      return;
    }

    this.branchGalleryTimerId = window.setInterval(() => {
      this.rotateBranchGalleryImage();
    }, this.branchGalleryIntervalMs);
  }

  ngOnDestroy(): void {
    if (this.branchGalleryTimerId !== undefined) {
      window.clearInterval(this.branchGalleryTimerId);
    }

    if (this.statusTimerId !== undefined) {
      window.clearInterval(this.statusTimerId);
    }

    if (this.branchGalleryTransitionTimerId !== undefined) {
      window.clearTimeout(this.branchGalleryTransitionTimerId);
    }
  }

  private preloadBranchGalleryImages(): void {
    for (const imageData of this.branchGalleryImages) {
      if (this.loadedBranchGalleryImages.has(imageData.src) || this.failedBranchGalleryImages.has(imageData.src)) {
        continue;
      }

      const image = new Image();
      image.onload = () => {
        this.loadedBranchGalleryImages.add(imageData.src);
      };
      image.onerror = () => {
        this.failedBranchGalleryImages.add(imageData.src);
        console.warn('Carousel image failed to load:', imageData.src);
      };
      image.src = imageData.src;
    }
  }

  private rotateBranchGalleryImage(): void {
    const nextIndex = this.findNextLoadedBranchImageIndex();
    if (nextIndex === null) {
      return;
    }

    this.previousBranchImage = this.currentBranchImage;
    this.currentBranchImage = this.branchGalleryImages[nextIndex];
    this.isBranchImageTransitioning = true;

    if (this.branchGalleryTransitionTimerId !== undefined) {
      window.clearTimeout(this.branchGalleryTransitionTimerId);
    }

    this.branchGalleryTransitionTimerId = window.setTimeout(() => {
      this.isBranchImageTransitioning = false;
    }, this.branchGalleryFadeDurationMs);

    this.branchGalleryIndex = nextIndex;
  }

  private findNextLoadedBranchImageIndex(): number | null {
    const totalImages = this.branchGalleryImages.length;

    for (let offset = 1; offset <= totalImages; offset++) {
      const candidateIndex = (this.branchGalleryIndex + offset) % totalImages;
      const candidateImage = this.branchGalleryImages[candidateIndex];

      if (candidateImage.src === this.currentBranchImage.src) {
        continue;
      }

      if (this.loadedBranchGalleryImages.has(candidateImage.src)) {
        return candidateIndex;
      }
    }

    return null;
  }

  selectBranch(branchId: BranchId): void {
    this.selectedBranch = branchId;

    const anchorId = window.location.hash.replace('#', '').trim();
    if (!anchorId || anchorId === 'start') {
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.scrollToAnchorWithOffset(anchorId);
      });
    });
  }

  get activeBranch(): BranchContent {
    return this.branches[this.selectedBranch];
  }

  get otherBranch(): BranchContent {
    return this.selectedBranch === 'swiatniki'
      ? this.branches.mogilany
      : this.branches.swiatniki;
  }

  get isSelectedBranchOpen(): boolean {
    return this.getBranchStatus(this.activeBranch, this.currentTime).isOpen;
  }

  get branchStatusText(): string {
    const status = this.getBranchStatus(this.activeBranch, this.currentTime);

    if (status.isOpen) {
      return status.closeTimeText
        ? `Otwarte teraz · Zamknięte o ${status.closeTimeText}`
        : 'Otwarte teraz';
    }

    return status.openTimeText
      ? `Zamknięte teraz · Otwarte od ${status.openTimeText}`
      : 'Zamknięte teraz';
  }

  get branchStatusClass(): string {
    return this.isSelectedBranchOpen ? 'is-open' : 'is-closed';
  }

  private isBranchOpen(branch: BranchContent, now: Date): boolean {
    return this.getBranchStatus(branch, now).isOpen;
  }

  private getBranchStatus(branch: BranchContent, now: Date): {
    isOpen: boolean;
    isSoonClosing: boolean;
    isSoonOpening: boolean;
    closeTimeText: string | null;
    openTimeText: string | null;
  } {
    const warsawWeekday = this.getWarsawWeekday(now);
    const currentMinutes = this.getWarsawMinutes(now);
    const todayPeriods = this.getDayOpeningPeriods(branch, warsawWeekday);

    for (const period of todayPeriods) {
      const openMinutes = this.parseTimeToMinutes(period.open);
      const closeMinutes = this.parseTimeToMinutes(period.close);

      if (currentMinutes >= openMinutes && currentMinutes < closeMinutes) {
        const minutesToClose = closeMinutes - currentMinutes;
        return {
          isOpen: true,
          isSoonClosing: minutesToClose <= this.statusSoonThresholdMinutes,
          isSoonOpening: false,
          closeTimeText:
            minutesToClose <= this.statusSoonThresholdMinutes ? this.formatMinutesAsTime(closeMinutes) : null,
          openTimeText: null
        };
      }
    }

    const nextOpening = this.getNextOpening(branch, warsawWeekday, currentMinutes);

    return {
      isOpen: false,
      isSoonClosing: false,
      isSoonOpening:
        nextOpening !== null && nextOpening.minutesToOpen <= this.statusSoonThresholdMinutes,
      closeTimeText: null,
      openTimeText:
        nextOpening !== null && nextOpening.minutesToOpen <= this.statusSoonThresholdMinutes
          ? this.formatMinutesAsTime(nextOpening.openMinutes)
          : null
    };
  }

  private getNextOpening(
    branch: BranchContent,
    currentWeekday: WeekdayNumber,
    currentMinutes: number
  ): { minutesToOpen: number; openMinutes: number } | null {
    const minutesInDay = 24 * 60;

    for (let dayOffset = 0; dayOffset <= 7; dayOffset++) {
      const dayNumber = (((currentWeekday - 1 + dayOffset) % 7) + 1) as WeekdayNumber;
      const dayPeriods = this.getDayOpeningPeriods(branch, dayNumber);

      for (const period of dayPeriods) {
        const openMinutes = this.parseTimeToMinutes(period.open);

        if (dayOffset === 0 && openMinutes <= currentMinutes) {
          continue;
        }

        return {
          minutesToOpen: dayOffset * minutesInDay + (openMinutes - currentMinutes),
          openMinutes
        };
      }
    }

    return null;
  }

  private formatMinutesAsTime(totalMinutes: number): string {
    const normalizedMinutes = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
    const hours = Math.floor(normalizedMinutes / 60);
    const minutes = normalizedMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private getDayOpeningPeriods(branch: BranchContent, weekday: WeekdayNumber): OpeningPeriod[] {
    return branch.openingPeriods
      .filter((period) => period.days.includes(weekday))
      .sort((a, b) => this.parseTimeToMinutes(a.open) - this.parseTimeToMinutes(b.open));
  }

  private getWarsawWeekday(now: Date): WeekdayNumber {
    const weekday = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Warsaw',
      weekday: 'short'
    }).format(now);

    const weekdayMap: Record<string, WeekdayNumber> = {
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
      Sun: 7
    };

    return weekdayMap[weekday] ?? 1;
  }

  private getWarsawMinutes(now: Date): number {
    const hour = Number(new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Warsaw',
      hour: '2-digit',
      hourCycle: 'h23'
    }).format(now));
    const minute = Number(new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Warsaw',
      minute: '2-digit'
    }).format(now));

    return hour * 60 + minute;
  }

  private parseTimeToMinutes(value: string): number {
    const [hourPart = '0', minutePart = '0'] = value.split(':');
    return Number(hourPart) * 60 + Number(minutePart);
  }

  private scrollToAnchorWithOffset(anchorId: string): void {
    const target = document.getElementById(anchorId);
    if (!target) {
      return;
    }

    const topbar = document.querySelector('.topbar') as HTMLElement | null;
    const branchSticky = document.querySelector('.branch-sticky') as HTMLElement | null;
    const topbarHeight = topbar?.getBoundingClientRect().height ?? 0;
    const branchStickyHeight = branchSticky?.getBoundingClientRect().height ?? 0;
    const offset = topbarHeight + branchStickyHeight + 12;

    const targetTop = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'auto'
    });
  }

  private createBranchGalleryImages(baseNames: string[]): BranchGalleryImage[] {
    return baseNames.map((baseName) => {
      const optimizedBasePath = `assets/branch-gallery/optimized/${baseName}`;
      return {
        src: `${optimizedBasePath}-1280.webp`,
        srcSet: `${optimizedBasePath}-1280.webp 1280w, ${optimizedBasePath}-2560.webp 2560w`
      };
    });
  }

}
